import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import CabPage from "../CabPage";
import MobileNav from "../account/MobileNav";
import { useCabUser } from "../CabShell";
import { Icon } from "../account/icons";
import { Dialog } from "../fleet/ui";
import { mediaUrl } from "../account/media";
import { useAuth } from "../../AuthProvider";
import { clearAuthTokens } from "../../utils/operatorAuth";
import "../css/settings.css";

const API = process.env.REACT_APP_API_URL;
const authHeaders = () => ({ Authorization: `JWT ${window.localStorage.getItem("access")}` });

const ROLES = [
  [1, "Грузовладелец"],
  [2, "Перевозчик"],
  [3, "Экспедитор"],
  [4, "Экспедитор-перевозчик"],
];
const TYPES = [
  [1, "ОсОО"],
  [2, "ИП"],
  [3, "ОАО"],
];
const COUNTRIES = ["Кыргызстан", "Казахстан", "Россия", "Узбекистан", "Китай"];
const EMPLOYEES = ["1-15", "16-50", "51-150", "151-500", "501-1500", "1501-5000"];
const ORDERS_MONTH = ["1-50", "51-100", "101-500", "501-1000", "1001-5000", "5001-100000"];
const VEHICLES = ["1-5", "6-15", "16-50", "51-100", "101-500", "501-1000"];

const TABS = [
  { key: "profile", label: "Профиль компании", icon: "building" },
  { key: "notifications", label: "Уведомления", icon: "bell" },
  { key: "region", label: "Язык и регион", icon: "globe" },
  { key: "security", label: "Безопасность", icon: "shieldPlain" },
  { key: "team", label: "Команда и доступ", icon: "users" },
  { key: "api", label: "API и интеграции", icon: "link" },
  { key: "branding", label: "Брендинг", icon: "palette" },
];

const MAX_IMG = 5 * 1024 * 1024;

const TIMEZONES = [
  ["Asia/Bishkek", "(GMT+06:00) Бишкек"],
  ["Asia/Almaty", "(GMT+05:00) Алматы"],
  ["Europe/Moscow", "(GMT+03:00) Москва"],
  ["Asia/Shanghai", "(GMT+08:00) Пекин"],
  ["Asia/Tashkent", "(GMT+05:00) Ташкент"],
];
const CURRENCIES = [
  ["KGS", "Кыргызский сом (сом)"],
  ["KZT", "Казахстанский тенге (₸)"],
  ["USD", "Доллар США ($)"],
];
const INDUSTRIES = ["Грузоперевозки и логистика", "Производство", "Торговля", "Строительство"];
const LANGS = [
  ["ru", "Русский"],
  ["en", "English"],
  ["ky", "Кыргызча"],
];
const DEFAULT_PREFS = { language: "ru", notify_requests: true, notify_messages: true, notify_system: true, notify_email: true };

const toDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const initialsOf = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "—";

const Soon = ({ children }) => (
  <p className="local-notice" style={{ marginTop: 16, color: "#889abe", fontSize: 13 }}>
    {children}
  </p>
);

export default function SettingsPage() {
  const location = useLocation();
  const user = useSelector((s) => s.auth.user) || {};
  const { companyProfileData, updateProfile } = useAuth();
  const { name: accountName, roleLabel } = useCabUser();
  const profile = companyProfileData || null;
  const cp = profile?.company_profile || null;

  const initialTab = new URLSearchParams(location.search).get("tab");
  const [tab, setTab] = useState(TABS.some((t) => t.key === initialTab) ? initialTab : "profile");
  const [form, setForm] = useState(null);
  const [imgs, setImgs] = useState({}); // changed images: key -> dataURL | null
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // { kind: "preview" | "doc" | "docs", key? }
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [prefsBusy, setPrefsBusy] = useState(false);
  const toastTimer = useRef(null);
  const logoInput = useRef(null);
  const docInputs = useRef({});

  const showToast = (t) => {
    setToast(t);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 4500);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  useEffect(() => {
    axios
      .get(`${API}/auth/preferences/`, { headers: authHeaders() })
      .then((r) => setPrefs({ ...DEFAULT_PREFS, ...r.data }))
      .catch(() => {});
  }, []);

  const baseForm = useMemo(
    () => ({
      company_name: cp?.company_name || "",
      INN: cp?.INN || "",
      role: cp?.role != null ? String(cp.role) : "",
      type: cp?.type != null ? String(cp.type) : "",
      phone: cp?.phone || "",
      email: cp?.email || "",
      website: cp?.website || "",
      address: cp?.address || "",
      country: cp?.country || "",
      city: cp?.city || "",
      description: cp?.description || "",
      founded_year: cp?.founded_year != null ? String(cp.founded_year) : "",
      linkedin: cp?.linkedin || "",
      timezone: cp?.timezone || "",
      currency: cp?.currency || "",
      industry: cp?.industry || "",
      employers_number: cp?.employers_number || "",
      orders_month: cp?.orders_month || "",
      vehicles_numbers: cp?.vehicles_numbers || "",
      telegram_nickname: profile?.telegram_nickname || "",
      whatsapp_number: profile?.whatsapp_number || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
    }),
    [cp, profile, user.first_name, user.last_name],
  );

  useEffect(() => {
    setForm(baseForm);
    setImgs({});
  }, [baseForm]);

  const f = form || baseForm;
  const set = (k) => (e) => setForm((s) => ({ ...(s || baseForm), [k]: e.target.value }));

  const logoSrc = "profile_images" in imgs ? imgs.profile_images : mediaUrl(cp?.profile_images);

  const pickImage = async (key, file, label) => {
    if (!file) return;
    if (file.size > MAX_IMG) return showToast(key === "profile_images" ? "Размер логотипа должен быть не более 5 МБ" : `${label}: размер должен быть не более 5 МБ`);
    if (!["image/png", "image/jpeg"].includes(file.type)) return showToast(key === "profile_images" ? "Выберите JPG или PNG" : `${label}: выберите JPG или PNG`);
    try {
      const url = await toDataUrl(file);
      setImgs((s) => ({ ...s, [key]: url }));
      showToast(key === "profile_images" ? "Логотип выбран. Сохраните изменения." : `Файл «${label}» выбран. Сохраните изменения.`);
    } catch {
      showToast("Не удалось прочитать файл");
    }
  };

  const cancel = () => {
    setForm(baseForm);
    setImgs({});
    showToast("Несохранённые изменения отменены");
  };

  const submit = async (e) => {
    e.preventDefault();
    const yearInput = e.currentTarget.elements.year;
    if (f.founded_year) {
      const y = Number(f.founded_year);
      if (!/^\d{4}$/.test(f.founded_year) || y < 1800 || y > new Date().getFullYear()) {
        yearInput.setCustomValidity("Укажите корректный год основания");
        yearInput.reportValidity();
        return;
      }
    }
    setSaving(true);
    try {
      const headers = authHeaders();
      // 1. personal data (djoser user)
      if (user.id && (f.first_name !== (user.first_name || "") || f.last_name !== (user.last_name || ""))) {
        await axios.patch(`${API}/auth/users/${user.id}/`, { first_name: f.first_name, last_name: f.last_name }, { headers });
      }
      // 2. company profile + messengers
      const company = {
        company_name: f.company_name,
        INN: f.INN,
        phone: f.phone,
        email: f.email,
        website: f.website,
        address: f.address,
        country: f.country,
        city: f.city,
        role: f.role ? Number(f.role) : null,
        type: f.type ? Number(f.type) : null,
        description: f.description || null,
        founded_year: f.founded_year ? Number(f.founded_year) : null,
        linkedin: f.linkedin || null,
        timezone: f.timezone || null,
        currency: f.currency || null,
        industry: f.industry || null,
        employers_number: f.employers_number || null,
        orders_month: [1, 3, 4].includes(Number(f.role)) ? f.orders_month || null : null,
        vehicles_numbers: [2, 4].includes(Number(f.role)) ? f.vehicles_numbers || null : null,
      };
      ["profile_images", "license", "certificate", "insurance"].forEach((k) => {
        if (k in imgs) company[k] = imgs[k];
      });
      const body = { company_profile: company, whatsapp_number: f.whatsapp_number, telegram_nickname: f.telegram_nickname };
      if (profile?.id) await axios.patch(`${API}/auth/profile/${profile.id}/`, body, { headers });
      else await axios.post(`${API}/auth/profile/`, body, { headers });
      showToast("Изменения сохранены");
      updateProfile();
    } catch (err) {
      const d = err?.response?.data;
      const msg = d && typeof d === "object" ? Object.values(d).flat().filter((x) => typeof x === "string").join(" ") : "";
      showToast(`Не удалось сохранить${msg ? `: ${msg}` : ""}`);
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await axios.post(
        `${API}/auth/users/set_password/`,
        { current_password: pw.current, new_password: pw.next },
        { headers: authHeaders() },
      );
      setPw({ current: "", next: "" });
      showToast("Пароль изменён");
    } catch (err) {
      const d = err?.response?.data;
      const msg = d && typeof d === "object" ? Object.values(d).flat().filter((x) => typeof x === "string").join(" ") : "";
      showToast(`Не удалось изменить пароль${msg ? `: ${msg}` : ""}`);
    } finally {
      setPwBusy(false);
    }
  };

  const logout = () => {
    clearAuthTokens();
    window.location.href = "/login";
  };

  const countries = COUNTRIES.includes(f.country) || !f.country ? COUNTRIES : [f.country, ...COUNTRIES];
  const roleN = Number(f.role);
  const docSrc = (k) => ("" + (k in imgs ? imgs[k] || "" : cp?.[k] || "")) || "";

  const docs = [
    { key: "certificate", title: "Свидетельство о регистрации" },
    { key: "license", title: "Лицензия на перевозки" },
    { key: "insurance", title: "Страховой полис" },
  ];

  const profileForm = (
    <form className="settings-grid" id="profile-form" onSubmit={submit}>
      <section className="panel company-info">
        <header>
          <div>
            <h2>Информация о компании</h2>
            <p>Эти данные отображаются для других пользователей на платформе.</p>
          </div>
          <button className="button subtle preview-button" type="button" onClick={() => setDialog({ kind: "preview" })}>
            <Icon name="eye" />
            Посмотреть профиль
          </button>
        </header>
        <div className="logo-upload">
          <div className="logo-box">
            {logoSrc ? <img alt="Логотип компании" src={logoSrc} id="logo-image" /> : <span id="company-avatar">{initialsOf(f.company_name)}</span>}
          </div>
          <div>
            <p>Логотип компании</p>
            <small>JPG или PNG. Размер до 5 МБ.</small>
            <div className="upload-actions">
              <button className="button primary" type="button" onClick={() => logoInput.current?.click()}>
                Загрузить логотип
              </button>
              <button
                className="button muted"
                type="button"
                onClick={() => {
                  setImgs((s) => ({ ...s, profile_images: null }));
                  showToast("Логотип сброшен. Сохраните изменения.");
                }}
              >
                Удалить
              </button>
            </div>
            <input
              accept="image/jpeg,image/png"
              hidden
              ref={logoInput}
              type="file"
              onChange={(e) => {
                pickImage("profile_images", e.target.files[0], "Логотип");
                e.target.value = "";
              }}
            />
          </div>
        </div>
        <div className="form-grid">
          <label className="field">
            <span>
              Название компании <b>*</b>
            </span>
            <span className="control">
              <input name="company" required type="text" value={f.company_name} onChange={set("company_name")} />
            </span>
          </label>
          <label className="field">
            <span>ИНН / БИН</span>
            <span className="control">
              <input name="tax" type="text" value={f.INN} onChange={set("INN")} />
            </span>
          </label>
          <label className="field">
            <span>Тип компании</span>
            <span className="control select">
              <select name="role" value={f.role} onChange={set("role")}>
                <option value="">Не указан</option>
                {ROLES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <Icon name="chevron" />
            </span>
          </label>
          <label className="field">
            <span>Организационная форма</span>
            <span className="control select">
              <select name="type" value={f.type} onChange={set("type")}>
                <option value="">Не указана</option>
                {TYPES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <Icon name="chevron" />
            </span>
          </label>
          <label className="field">
            <span>Год основания</span>
            <span className="control">
              <input
                name="year"
                type="text"
                inputMode="numeric"
                value={f.founded_year}
                onChange={(e) => {
                  e.target.setCustomValidity("");
                  set("founded_year")(e);
                }}
              />
            </span>
          </label>
        </div>
        <label className="field description">
          <span>Описание компании</span>
          <span className="textarea-wrap">
            <textarea maxLength="500" name="description" value={f.description} onChange={set("description")} />
            <small id="char-count">{f.description.length}/500</small>
          </span>
        </label>
        <h3>Контактная информация</h3>
        <div className="form-grid contacts">
          <label className="field">
            <span>
              Телефон <b>*</b>
            </span>
            <span className="control">
              <Icon name="phone" />
              <input name="phone" required type="tel" value={f.phone} onChange={set("phone")} />
            </span>
          </label>
          <label className="field">
            <span>
              Email <b>*</b>
            </span>
            <span className="control">
              <Icon name="mail" />
              <input name="email" required type="email" value={f.email} onChange={set("email")} />
            </span>
          </label>
          <label className="field">
            <span>Сайт</span>
            <span className="control">
              <Icon name="globe" />
              <input name="website" type="text" value={f.website} onChange={set("website")} />
            </span>
          </label>
          <label className="field">
            <span>Адрес</span>
            <span className="control">
              <Icon name="pin" />
              <input name="address" type="text" value={f.address} onChange={set("address")} />
            </span>
          </label>
        </div>
        <footer className="form-actions">
          <button className="button muted" type="button" onClick={cancel}>
            Отмена
          </button>
          <button className="button primary" type="submit" disabled={saving}>
            {saving ? "Сохранение…" : "Сохранить изменения"}
          </button>
        </footer>
      </section>

      <div className="right-column">
        <section className="panel basic">
          <h3>Основная информация</h3>
          <div className="form-grid">
            <label className="field">
              <span>Страна регистрации</span>
              <span className="control select">
                <select name="country" value={f.country} onChange={set("country")}>
                  <option value="">Не указана</option>
                  {countries.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <Icon name="chevron" />
              </span>
            </label>
            <label className="field">
              <span>Город</span>
              <span className="control">
                <input name="city" type="text" value={f.city} onChange={set("city")} />
              </span>
            </label>
            <label className="field">
              <span>Часовой пояс</span>
              <span className="control select">
                <select name="timezone" value={f.timezone} onChange={set("timezone")}>
                  <option value="">Не указан</option>
                  {TIMEZONES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
                <Icon name="chevron" />
              </span>
            </label>
            <label className="field">
              <span>Валюта</span>
              <span className="control select">
                <select name="currency" value={f.currency} onChange={set("currency")}>
                  <option value="">Не указана</option>
                  {CURRENCIES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
                <Icon name="chevron" />
              </span>
            </label>
          </div>
        </section>
        <section className="panel extra">
          <h3>Дополнительная информация</h3>
          <div className="form-grid">
            <label className="field">
              <span>Размер компании</span>
              <span className="control select">
                <select name="size" value={f.employers_number} onChange={set("employers_number")}>
                  <option value="">Не указан</option>
                  {(EMPLOYEES.includes(f.employers_number) || !f.employers_number ? EMPLOYEES : [f.employers_number, ...EMPLOYEES]).map((v) => (
                    <option key={v} value={v}>
                      {v} сотрудников
                    </option>
                  ))}
                </select>
                <Icon name="chevron" />
              </span>
            </label>
            <label className="field">
              <span>Сфера деятельности</span>
              <span className="control select">
                <select name="industry" value={f.industry} onChange={set("industry")}>
                  <option value="">Не указана</option>
                  {(INDUSTRIES.includes(f.industry) || !f.industry ? INDUSTRIES : [f.industry, ...INDUSTRIES]).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
                <Icon name="chevron" />
              </span>
            </label>
            {[2, 4].includes(roleN) && (
              <label className="field">
                <span>Транспортных средств</span>
                <span className="control select">
                  <select name="vehicles" value={f.vehicles_numbers} onChange={set("vehicles_numbers")}>
                    <option value="">Не указано</option>
                    {(VEHICLES.includes(f.vehicles_numbers) || !f.vehicles_numbers ? VEHICLES : [f.vehicles_numbers, ...VEHICLES]).map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                  <Icon name="chevron" />
                </span>
              </label>
            )}
            {[1, 3, 4].includes(roleN) && (
              <label className="field">
                <span>Заказов в месяц</span>
                <span className="control select">
                  <select name="orders_month" value={f.orders_month} onChange={set("orders_month")}>
                    <option value="">Не указано</option>
                    {(ORDERS_MONTH.includes(f.orders_month) || !f.orders_month ? ORDERS_MONTH : [f.orders_month, ...ORDERS_MONTH]).map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                  <Icon name="chevron" />
                </span>
              </label>
            )}
          </div>
        </section>
        <section className="panel socials">
          <h3>Контактное лицо и мессенджеры</h3>
          <div className="social-grid">
            <label className="field">
              <span>Имя</span>
              <span className="control">
                <Icon name="users" />
                <input name="first_name" type="text" value={f.first_name} onChange={set("first_name")} />
              </span>
            </label>
            <label className="field">
              <span>Фамилия</span>
              <span className="control">
                <Icon name="users" />
                <input name="last_name" type="text" value={f.last_name} onChange={set("last_name")} />
              </span>
            </label>
            <label className="field">
              <span>Telegram</span>
              <span className="control">
                <Icon name="chat" />
                <input name="telegram" type="text" value={f.telegram_nickname} onChange={set("telegram_nickname")} />
              </span>
            </label>
            <label className="field">
              <span>WhatsApp</span>
              <span className="control">
                <Icon name="phone" />
                <input name="whatsapp" type="text" value={f.whatsapp_number} onChange={set("whatsapp_number")} />
              </span>
            </label>
            <label className="field">
              <span>LinkedIn</span>
              <span className="control">
                <Icon name="link" />
                <input name="linkedin" type="text" value={f.linkedin} onChange={set("linkedin")} />
              </span>
            </label>
          </div>
        </section>
        <section className="panel documents">
          <header>
            <div>
              <h3>Документы компании</h3>
              <p>Эти документы могут быть запрошены при проверке компании.</p>
            </div>
            <button className="button subtle" type="button" onClick={() => setDialog({ kind: "docs" })}>
              Загрузить документ
            </button>
          </header>
          <div id="documents-list">
            {docs.map((d) => {
              const src = docSrc(d.key);
              return (
                <div className="doc-row" key={d.key}>
                  <span className="doc-icon">
                    <Icon name="doc" />
                  </span>
                  <div>
                    <strong>{d.title}</strong>
                    <small>
                      {src ? (
                        <a href={mediaUrl(src)} target="_blank" rel="noreferrer">
                          {d.key in imgs ? "Выбран новый файл (не сохранён)" : "Загружен · открыть"}
                        </a>
                      ) : (
                        "Не загружен · JPG или PNG до 5 МБ"
                      )}
                    </small>
                  </div>
                  <span className={`doc-status${d.key in imgs ? " pending" : ""}`}>{d.key in imgs ? "◆ Не сохранён" : src ? "◆ Загружен" : "◆ Не загружен"}</span>
                  <button aria-label={`Действия с документом ${d.title}`} className="button more" type="button" onClick={() => setDialog({ kind: "doc", key: d.key })}>
                    ···
                  </button>
                  <input
                    accept="image/jpeg,image/png"
                    hidden
                    ref={(el) => (docInputs.current[d.key] = el)}
                    type="file"
                    onChange={(e) => {
                      pickImage(d.key, e.target.files[0], d.title);
                      e.target.value = "";
                    }}
                  />
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </form>
  );

  const savePrefs = async (patch, okText) => {
    setPrefsBusy(true);
    try {
      const r = await axios.patch(`${API}/auth/preferences/`, patch, { headers: authHeaders() });
      setPrefs({ ...DEFAULT_PREFS, ...r.data });
      showToast(okText);
    } catch {
      showToast("Не удалось сохранить настройки");
    } finally {
      setPrefsBusy(false);
    }
  };
  const [notifDraft, setNotifDraft] = useState(null);
  const [langDraft, setLangDraft] = useState(null);
  const nd = notifDraft || prefs;
  const ld = langDraft || prefs.language;

  const other = {
    notifications: (
      <>
        <h2>Уведомления</h2>
        <form
          id="preferences"
          style={{ display: "grid", gap: 12, margin: "14px 0" }}
          onSubmit={(e) => {
            e.preventDefault();
            const { notify_requests, notify_messages, notify_system } = nd;
            savePrefs({ notify_requests, notify_messages, notify_system }, "Настройки сохранены");
            setNotifDraft(null);
          }}
        >
          {[
            ["notify_requests", "Запросы на перевозку"],
            ["notify_messages", "Новые сообщения"],
            ["notify_system", "Системные события"],
          ].map(([k, l]) => (
            <label key={k} style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input type="checkbox" name={k} checked={nd[k] !== false} onChange={(e) => setNotifDraft({ ...nd, [k]: e.target.checked })} /> {l}
            </label>
          ))}
          <label style={{ display: "flex", gap: 10, alignItems: "center", opacity: 0.55 }} title="Скоро: рассылка по email пока не подключена">
            <input type="checkbox" name="email" disabled /> Уведомления по email (скоро)
          </label>
          <div>
            <button className="button primary" type="submit" disabled={prefsBusy}>
              Сохранить настройки
            </button>
          </div>
        </form>
        <Soon>Отключённые типы не помечаются как новые и не попадают в счётчик у колокольчика.</Soon>
      </>
    ),
    region: (
      <>
        <h2>Язык и регион</h2>
        <p>Страна, часовой пояс и валюта указаны в профиле компании.</p>
        <form
          id="preferences"
          style={{ display: "grid", gap: 12, maxWidth: 320, margin: "14px 0" }}
          onSubmit={(e) => {
            e.preventDefault();
            savePrefs({ language: ld }, "Предпочтение сохранено");
            setLangDraft(null);
          }}
        >
          <label style={{ display: "grid", gap: 6 }}>
            Язык интерфейса
            <span className="control select">
              <select name="language" value={ld} onChange={(e) => setLangDraft(e.target.value)}>
                {LANGS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <Icon name="chevron" />
            </span>
          </label>
          <div>
            <button className="button primary" type="submit" disabled={prefsBusy}>
              Сохранить предпочтение
            </button>
          </div>
        </form>
        <Soon>Интерфейс пока доступен только на русском: выбор сохраняется в профиле и будет применён после добавления переводов.</Soon>
      </>
    ),
    security: (
      <>
        <h2>Безопасность</h2>
        {user.is_password_exists === false ? (
          <p>Для этого аккаунта пароль не задан — вход выполняется по коду.</p>
        ) : (
          <form onSubmit={changePassword} style={{ display: "grid", gap: 12, maxWidth: 360, margin: "14px 0" }}>
            <label className="field">
              <span>Текущий пароль</span>
              <span className="control">
                <input type="password" required autoComplete="current-password" value={pw.current} onChange={(e) => setPw((s) => ({ ...s, current: e.target.value }))} />
              </span>
            </label>
            <label className="field">
              <span>Новый пароль</span>
              <span className="control">
                <input type="password" required minLength={8} autoComplete="new-password" value={pw.next} onChange={(e) => setPw((s) => ({ ...s, next: e.target.value }))} />
              </span>
            </label>
            <button className="button primary" type="submit" disabled={pwBusy}>
              {pwBusy ? "Сохранение…" : "Сменить пароль"}
            </button>
          </form>
        )}
        <div style={{ margin: "18px 0 6px", display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="button" type="button" onClick={logout}>
            Выйти из аккаунта
          </button>
          <button className="button" type="button" disabled title="Скоро">
            Двухфакторная аутентификация (скоро)
          </button>
          <button className="button" type="button" disabled title="Скоро">
            Активные сеансы (скоро)
          </button>
        </div>
        <Soon>Двухфакторная аутентификация и управление сеансами требуют серверной поддержки и пока недоступны.</Soon>
      </>
    ),
    team: (
      <>
        <h2>Команда и доступ</h2>
        <p>
          {accountName} · Администратор: {[user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || "—"} ({roleLabel})
        </p>
        <div style={{ margin: "14px 0" }}>
          <button className="button" type="button" disabled title="Скоро">
            Пригласить сотрудника (скоро)
          </button>
        </div>
        <Soon>Приглашение сотрудников и назначение прав доступа требуют серверной поддержки и пока недоступны.</Soon>
      </>
    ),
    api: (
      <>
        <h2>API и интеграции</h2>
        <p>Подключение интеграций и создание API-ключей.</p>
        <div style={{ margin: "14px 0" }}>
          <button className="button" type="button" disabled title="Скоро">
            Создать API-ключ (скоро)
          </button>
        </div>
        <Soon>API-ключи и интеграции требуют серверной поддержки и пока недоступны.</Soon>
      </>
    ),
    branding: (
      <>
        <h2>Брендинг</h2>
        <p>Логотип компании можно изменить во вкладке «Профиль компании».</p>
        <button className="button" type="button" onClick={() => setTab("profile")}>
          Перейти к логотипу
        </button>
      </>
    ),
  };

  const docTitle = (k) => docs.find((d) => d.key === k)?.title;

  return (
    <CabPage name="settings">
      <main id="main">
        <div className="page-heading">
          <h1>Настройки</h1>
          <p>Управляйте профилем компании, безопасностью, уведомлениями и интеграциями.</p>
        </div>
        <nav aria-label="Разделы настроек" className="settings-tabs" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} aria-selected={tab === t.key} className={tab === t.key ? "active" : ""} role="tab" type="button" onClick={() => setTab(t.key)}>
              <Icon name={t.icon} />
              {t.label}
            </button>
          ))}
        </nav>
        {tab === "profile" ? profileForm : <section className="panel" id="other-tab">{other[tab]}</section>}
      </main>
      {dialog?.kind === "preview" && (
        <Dialog title={f.company_name || "Компания"} onClose={() => setDialog(null)}>
          <p>
            <strong>{ROLES.find(([v]) => String(v) === f.role)?.[1] || ""}</strong>
            {f.type ? ` · ${TYPES.find(([v]) => String(v) === f.type)?.[1]}` : ""}
            {f.city || f.country ? ` · ${[f.city, f.country].filter(Boolean).join(", ")}` : ""}
          </p>
          {f.description && <p>{f.description}</p>}
          <p>
            {[f.phone, f.email, f.website].filter(Boolean).map((v, i) => (
              <React.Fragment key={v}>
                {i > 0 && <br />}
                {v}
              </React.Fragment>
            ))}
          </p>
        </Dialog>
      )}
      {dialog?.kind === "docs" && (
        <Dialog title="Загрузить документ" onClose={() => setDialog(null)}>
          <p>Выберите тип документа (JPG или PNG до 5 МБ).</p>
          <div style={{ display: "grid", gap: 10 }}>
            {docs.map((d) => (
              <button
                key={d.key}
                className="button"
                type="button"
                onClick={() => {
                  setDialog(null);
                  docInputs.current[d.key]?.click();
                }}
              >
                {d.title}
              </button>
            ))}
          </div>
        </Dialog>
      )}
      {dialog?.kind === "doc" && (
        <Dialog title={docTitle(dialog.key)} onClose={() => setDialog(null)}>
          {docSrc(dialog.key) ? (
            <p>
              {dialog.key in imgs ? "Выбран новый файл — сохраните изменения, чтобы отправить его на сервер." : "Документ загружен на сервер."}{" "}
              {!(dialog.key in imgs) && (
                <a href={mediaUrl(docSrc(dialog.key))} target="_blank" rel="noreferrer">
                  Открыть файл
                </a>
              )}
            </p>
          ) : (
            <p>Документ не загружен.</p>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              className="button"
              type="button"
              onClick={() => {
                const k = dialog.key;
                setDialog(null);
                docInputs.current[k]?.click();
              }}
            >
              {docSrc(dialog.key) ? "Заменить" : "Загрузить"}
            </button>
            {docSrc(dialog.key) && (
              <button
                className="button"
                type="button"
                onClick={() => {
                  setImgs((s) => ({ ...s, [dialog.key]: null }));
                  setDialog(null);
                  showToast("Документ удалён. Сохраните изменения.");
                }}
              >
                Удалить
              </button>
            )}
          </div>
        </Dialog>
      )}
      <div aria-live="polite" id="toast" role="status" className={toast ? "visible" : ""}>
        {toast}
      </div>
      <MobileNav />
    </CabPage>
  );
}
